import React, {
    CSSProperties,
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Clock3, MapPin, UsersRound } from 'lucide-react';
import { SmartScheduleEvent } from '@/features/smartSchedule/model';
import { useQuickMoveController } from '../QuickMove/QuickMoveContext';
import { QuickMoveEntry } from '../QuickMove/QuickMoveEntry';
import ScheduleMemberBadges from '../ScheduleMemberBadges';
import { resolveScheduleAppLink, resolveScheduleClassLog } from '../../../lib/scheduleMemberLink';
import { useCoachColor } from './CoachColorContext';
import {
    getEventEnd,
    getEventStart,
    getEventTone,
    getKindLabel,
    getStatusGroup,
    getStatusLabel,
    SmartResolvedStatus,
} from './utils';

type EventCardProps = {
    event: SmartScheduleEvent;
    onOpen: (event: SmartScheduleEvent) => void;
    variant: 'month' | 'week' | 'week-compact' | 'day' | 'agenda';
    className?: string;
    style?: CSSProperties;
    displayTime?: string;
    dayDetailLevel?: 1 | 2 | 3;
    continuesFromPreviousDay?: boolean;
    status?: SmartResolvedStatus;
    onQuickMoveOpen?: () => void;
};

type PreviewPosition = { top: number; left: number };

const getDetailValue = (event: SmartScheduleEvent, keys: string[]) => {
    const raw = (event.rawItems?.[0] || {}) as Record<string, unknown>;
    for (const key of keys) {
        const value = raw[key];
        if (typeof value === 'string' && value.trim()) return value;
    }
    return '';
};

const normalizePreviewText = (value = '') =>
    value
        .normalize('NFKC')
        .trim()
        .replace(/\s+/g, '')
        .replace(/[·,./()_-]/g, '')
        .toLocaleLowerCase('ko-KR');

const isSamePreviewText = (left = '', right = '') => {
    const normalizedLeft = normalizePreviewText(left);
    const normalizedRight = normalizePreviewText(right);
    if (!normalizedLeft || !normalizedRight) return false;
    const removeMemberSuffix = (value: string) => value.replace(/(?:회원님?|님)$/, '');
    return normalizedLeft === normalizedRight
        || removeMemberSuffix(normalizedLeft) === normalizedRight
        || normalizedLeft === removeMemberSuffix(normalizedRight);
};

const getGroupCapacityInfo = (event: SmartScheduleEvent) => {
    // Capacity belongs to a real group-session resource. Concurrent PT member
    // rows must never acquire group capacity merely because they share a slot.
    if (event.kind !== 'group' || !event.groupSessionId) {
        return { current: undefined, max: undefined, text: '' };
    }

    const current = event.currentCount;
    const max = event.maxCapacity;
    const text = typeof current === 'number' && typeof max === 'number'
        ? `${current}/${max}`
        : typeof current === 'number'
        ? `${current}`
        : '';

    return { current, max, text };
};

const EventPreview = ({
    event,
    position,
    id,
    status,
    canDrag,
}: {
    event: SmartScheduleEvent;
    position: PreviewPosition;
    id: string;
    status: SmartResolvedStatus;
    canDrag: boolean;
}) => {
    const start = getEventStart(event);
    const end = getEventEnd(event);
    const coachColor = useCoachColor(event.coachId);
    const tone = getEventTone(event);
    const kindLabel = getKindLabel(event);
    const statusLabel = getStatusLabel(event, status);
    const previewTitle = event.title || '일정';
    const members = Array.from(new Map(
        (event.memberNames || [])
            .filter(Boolean)
            .map((memberName) => [normalizePreviewText(memberName), memberName])
    ).values());
    const allMemberText = members.join(', ');
    const previewSubtitle = event.subtitle
        && !isSamePreviewText(event.subtitle, previewTitle)
        && !isSamePreviewText(event.subtitle, allMemberText)
        && !members.some((memberName) => isSamePreviewText(event.subtitle, memberName))
        ? event.subtitle
        : '';
    const additionalMembers = members.filter((memberName) => !isSamePreviewText(memberName, previewTitle));
    const memberText = additionalMembers.length > 3
        ? `${additionalMembers.slice(0, 3).join(', ')} 외 ${additionalMembers.length - 3}명`
        : additionalMembers.join(', ');
    const capacityText = getGroupCapacityInfo(event).text;
    const capacity = capacityText ? `${capacityText}명` : '';
    const room = getDetailValue(event, ['roomName', 'room', 'place', 'location']);
    // PT 수업만 회원 단위 스케줄이라 앱 연동/수업일지 상태를 판정할 수 있다.
    const memberStatusRows = (event.kind === 'private' ? event.rawItems || [] : [])
        .map((rawItem, index) => ({
            key: String(rawItem?._id || rawItem?.id || index),
            name: typeof rawItem?.alias === 'string' ? rawItem.alias : '',
            rawItem,
        }))
        .filter(
            ({ rawItem }) =>
                resolveScheduleAppLink(rawItem) !== 'unknown' || resolveScheduleClassLog(rawItem) !== 'none'
        );

    return createPortal(
        <div
            id={id}
            className="smart-schedule__event-preview"
            role="tooltip"
            style={{ top: position.top, left: position.left }}
        >
            <div className="smart-schedule__preview-heading">
                <span className={`smart-schedule__kind smart-schedule__kind--${tone}`}>{kindLabel}</span>
                <span className={`smart-schedule__status smart-schedule__status--${status}`}>
                    {statusLabel}
                </span>
            </div>
            <strong className="smart-schedule__preview-title">{previewTitle}</strong>
            {previewSubtitle && <p className="smart-schedule__preview-subtitle">{previewSubtitle}</p>}
            <dl className="smart-schedule__preview-list">
                <div>
                    <dt><Clock3 size={14} aria-hidden="true" /></dt>
                    <dd>
                        {start.format('M월 D일 (ddd) HH:mm')} –{' '}
                        {start.isSame(end, 'day') ? end.format('HH:mm') : end.format('M월 D일 (ddd) HH:mm')}
                    </dd>
                </div>
                <div>
                    <dt>강사</dt>
                    <dd>
                        <span
                            className="smart-schedule__coach-pill"
                            style={{ color: coachColor.foreground, background: coachColor.background, borderColor: coachColor.border }}
                        >
                            {event.coachName || '담당 강사 없음'}
                        </span>
                    </dd>
                </div>
                {(memberText || capacity) && (
                    <div>
                        <dt><UsersRound size={14} aria-hidden="true" /></dt>
                        <dd>{[memberText, capacity].filter(Boolean).join(' · ')}</dd>
                    </div>
                )}
                {(event.membershipName || event.sessionText) && (
                    <div>
                        <dt>수강</dt>
                        <dd>{[event.membershipName, event.sessionText].filter(Boolean).join(' · ')}</dd>
                    </div>
                )}
                {memberStatusRows.length > 0 && (
                    <div>
                        <dt>상태</dt>
                        <dd style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {memberStatusRows.map((row) => (
                                <ScheduleMemberBadges
                                    key={row.key}
                                    schedule={row.rawItem}
                                    memberName={memberStatusRows.length > 1 ? row.name : undefined}
                                />
                            ))}
                        </dd>
                    </div>
                )}
                {room && (
                    <div>
                        <dt><MapPin size={14} aria-hidden="true" /></dt>
                        <dd>{room}</dd>
                    </div>
                )}
            </dl>
            <span className="smart-schedule__preview-hint">
                {canDrag ? '클릭하면 상세 · 끌어서 시간 옮기기' : '클릭하면 상세 내용을 볼 수 있어요'}
            </span>
        </div>,
        document.body
    );
};

type ActivePreview = {
    event: SmartScheduleEvent;
    status: SmartResolvedStatus;
    position: PreviewPosition;
    id: string;
    canDrag: boolean;
};

type PreviewController = {
    show: (
        event: SmartScheduleEvent,
        status: SmartResolvedStatus,
        anchor: HTMLButtonElement,
        delay?: number,
        canDrag?: boolean
    ) => void;
    hide: (anchor?: HTMLButtonElement) => void;
    suspend: () => void;
    resume: () => void;
};

const PreviewContext = createContext<PreviewController>({
    show: () => undefined,
    hide: () => undefined,
    suspend: () => undefined,
    resume: () => undefined,
});

export const useSmartSchedulePreviewController = () => useContext(PreviewContext);

/** One timer and one portal serve every card on the board, including dense weeks. */
export const SmartSchedulePreviewProvider = ({ children }: { children: ReactNode }) => {
    const [activePreview, setActivePreview] = useState<ActivePreview | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingAnchorRef = useRef<HTMLButtonElement | null>(null);
    const activeAnchorRef = useRef<HTMLButtonElement | null>(null);
    const previewSequenceRef = useRef(0);
    const suspendedRef = useRef(false);

    const clearTimer = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        pendingAnchorRef.current = null;
    }, []);

    const clearActive = useCallback(() => {
        activeAnchorRef.current?.removeAttribute('aria-describedby');
        activeAnchorRef.current = null;
        setActivePreview(null);
    }, []);

    const hide = useCallback((anchor?: HTMLButtonElement) => {
        if (!anchor || pendingAnchorRef.current === anchor) clearTimer();
        if (!anchor || activeAnchorRef.current === anchor) clearActive();
    }, [clearActive, clearTimer]);

    const show = useCallback((
        event: SmartScheduleEvent,
        status: SmartResolvedStatus,
        anchor: HTMLButtonElement,
        delay = 180,
        canDrag = false
    ) => {
        if (suspendedRef.current) return;
        clearTimer();
        pendingAnchorRef.current = anchor;

        const displayPreview = () => {
            timerRef.current = null;
            pendingAnchorRef.current = null;
            if (suspendedRef.current || !anchor.isConnected) return;

            activeAnchorRef.current?.removeAttribute('aria-describedby');
            const rect = anchor.getBoundingClientRect();
            const width = Math.min(328, Math.max(0, window.innerWidth - 24));
            const estimatedHeight = 300;
            const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
            const top = rect.bottom + estimatedHeight + 12 < window.innerHeight
                ? rect.bottom + 8
                : Math.max(12, rect.top - estimatedHeight - 8);
            const safeId = String(event.id).replace(/[^a-zA-Z0-9_-]/g, '-');
            const id = `smart-event-preview-${safeId}-${previewSequenceRef.current += 1}`;
            activeAnchorRef.current = anchor;
            anchor.setAttribute('aria-describedby', id);
            setActivePreview({ event, status, position: { top, left }, id, canDrag });
        };

        if (delay <= 0) displayPreview();
        else timerRef.current = setTimeout(displayPreview, delay);
    }, [clearTimer]);

    const suspend = useCallback(() => { suspendedRef.current = true; hide(); }, [hide]);
    const resume = useCallback(() => { suspendedRef.current = false; }, []);

    useEffect(() => () => {
        clearTimer();
        activeAnchorRef.current?.removeAttribute('aria-describedby');
    }, [clearTimer]);

    useEffect(() => {
        if (activeAnchorRef.current && !activeAnchorRef.current.isConnected) clearActive();
    });

    useEffect(() => {
        if (!activePreview) return;
        const dismissPreview = () => hide();
        window.addEventListener('scroll', dismissPreview, true);
        window.addEventListener('resize', dismissPreview);
        return () => {
            window.removeEventListener('scroll', dismissPreview, true);
            window.removeEventListener('resize', dismissPreview);
        };
    }, [activePreview, hide]);

    const controller = useMemo(() => ({ show, hide, suspend, resume }), [hide, show, suspend, resume]);

    return (
        <PreviewContext.Provider value={controller}>
            {children}
            {activePreview && (
                <EventPreview
                    event={activePreview.event}
                    status={activePreview.status}
                    position={activePreview.position}
                    id={activePreview.id}
                    canDrag={activePreview.canDrag}
                />
            )}
        </PreviewContext.Provider>
    );
};

const EventCard = ({
    event,
    onOpen,
    variant,
    className = '',
    style,
    displayTime,
    dayDetailLevel = 2,
    continuesFromPreviousDay = false,
    status: suppliedStatus,
    onQuickMoveOpen,
}: EventCardProps) => {
    const quickMove = useQuickMoveController();
    const hasQuickMove = !!quickMove && (event.kind === 'private' || event.kind === 'general');
    const canDrag = variant === 'day' && !!quickMove?.getDragSource(event);
    const preview = useContext(PreviewContext);
    const coachColor = useCoachColor(event.coachId);
    const status = suppliedStatus || getStatusGroup(event);
    const statusLabel = getStatusLabel(event, status);
    const tone = getEventTone(event);
    const kindLabel = getKindLabel(event);
    const statusHasIcon = status === 'pending' || status === 'noshow';
    const isOpportunity = event.kind === 'opportunity';
    const isDayOverlapCompact = variant === 'day'
        && event.kind === 'private'
        && className.split(/\s+/).includes('is-stacked');
    const groupCapacityInfo = getGroupCapacityInfo(event);
    const capacity = groupCapacityInfo.text;

    const start = getEventStart(event);
    const end = getEventEnd(event);
    const time = displayTime || start.format('HH:mm');
    const primaryText = event.title || event.memberNames?.[0] || '일정';
    const ptSessionText = event.kind === 'private' ? event.sessionText : '';
    const classType = String(event.classType || '').toUpperCase();
    const groupedPrivateLabel = classType.includes('1ON2')
        ? '1:2 수업'
        : classType.includes('1ON3')
        ? '1:3 수업'
        : '';
    const weekPrimaryText = event.kind === 'group'
        ? groupedPrivateLabel || (event.groupSessionId ? primaryText : '그룹 수업')
        : event.kind === 'general' && primaryText === '일반 일정' && event.subtitle
        ? event.subtitle
        : primaryText;
    const weekDetailText = event.kind === 'general' ? weekPrimaryText : '';
    const groupApplicantCount = event.kind === 'group' ? groupCapacityInfo.current : undefined;
    const weekSupplement = groupApplicantCount !== undefined ? `${groupApplicantCount}명` : '';
    const firstMemberName = event.memberNames?.find(Boolean)
        || event.members?.find((member) => Boolean(member.name))?.name
        || '';
    const groupCapacity = groupCapacityInfo.text ? `${groupCapacityInfo.text}명` : '';
    const requestRawMemberName = getDetailValue(event, ['memberName', 'alias']);
    const requestTitleMemberName = primaryText
        .replace(/\s*(?:수업\s*)?(?:신청|변경\s*요청)(?:\s*대기)?$/u, '')
        .trim();
    const requestMemberName = firstMemberName
        || requestRawMemberName
        || (event.subtitle && !['신청 대기', '변경 요청'].includes(event.subtitle) ? event.subtitle : '')
        || requestTitleMemberName
        || '회원 미정';
    const opportunityRawMemberName = getDetailValue(event, ['memberName', 'alias']);
    const opportunityCategoryName = getDetailValue(event, ['categoryName', 'opportunityName']);
    const opportunityMemberName = firstMemberName
        || opportunityRawMemberName
        || (
            event.subtitle
            && !isSamePreviewText(event.subtitle, primaryText)
            && !isSamePreviewText(event.subtitle, opportunityCategoryName)
                ? event.subtitle
                : ''
        )
        || '회원 미정';
    const opportunityName = [opportunityCategoryName, primaryText, event.subtitle]
        .find((candidate) => candidate && !isSamePreviewText(candidate, opportunityMemberName))
        || '';
    const groupedPrivateBadge = groupedPrivateLabel.replace(' 수업', '');
    const groupMemberNames = (event.memberNames || []).filter(Boolean);
    const isInferredGroupedPrivateTitle = Boolean(
        groupedPrivateLabel
        && !event.groupSessionId
        && groupMemberNames.length > 1
        && groupMemberNames.every((memberName) =>
            normalizePreviewText(primaryText).includes(normalizePreviewText(memberName))
        )
    );
    const dayGroupTitle = isInferredGroupedPrivateTitle
        ? groupedPrivateLabel
        : event.title || groupedPrivateLabel || '그룹 수업';
    const dayBadgeLabel = status === 'noshow'
        ? '노쇼'
        : status === 'pending' || event.kind === 'request'
        ? '신청'
        : event.kind === 'private'
        ? 'PT'
        : event.kind === 'group'
        ? groupedPrivateBadge || '그룹'
        : event.kind === 'opportunity'
        ? '신규'
        : event.kind === 'general'
        ? '일반'
        : '';
    const dayLines = event.kind === 'private'
        ? [firstMemberName || primaryText || '회원 미정', time, ptSessionText]
        : event.kind === 'group'
        ? [dayGroupTitle, time, groupCapacity]
        : event.kind === 'opportunity'
        ? [opportunityMemberName, time, opportunityName]
        : event.kind === 'request'
        ? [time, requestMemberName, '']
        : event.kind === 'general'
        ? [primaryText || '일반 일정', time, event.subtitle && !isSamePreviewText(event.subtitle, primaryText) ? event.subtitle : '']
        : [time, '', ''];
    const accessiblePrimaryText = [primaryText, ptSessionText].filter(Boolean).join(' ');
    const accessibleDayText = dayLines
        .filter((line) => Boolean(line) && line !== time)
        .join(' ') || primaryText;
    const fullTimeLabel = start.isSame(end, 'day')
        ? `${start.format('M월 D일 HH:mm')}–${end.format('HH:mm')}`
        : `${start.format('M월 D일 HH:mm')}–${end.format('M월 D일 HH:mm')}`;

    const card = (
        <button
                type="button"
                data-quick-move-id={event.id}
                onPointerDown={() => preview.hide()}
                className={`smart-schedule__event smart-schedule__event--${variant} smart-schedule__event--${tone} smart-schedule__event-status--${status} ${variant === 'day' ? `is-day-detail-${dayDetailLevel}` : ''} ${canDrag ? 'is-quick-move-draggable' : ''} ${className}`}
                style={{
                    ...(hasQuickMove ? undefined : style),
                    touchAction: canDrag ? 'none' : undefined,
                    '--coach-bg': coachColor.background,
                    '--coach-text': coachColor.foreground,
                    '--coach-border': coachColor.border,
                } as CSSProperties}
                aria-label={`${time} ${kindLabel} ${variant === 'day' ? accessibleDayText : accessiblePrimaryText}, ${fullTimeLabel}, ${event.coachName || '담당 강사 없음'}, ${statusLabel}${groupApplicantCount !== undefined ? `, 신청 ${groupApplicantCount}명` : capacity ? `, 예약 ${capacity}명` : ''}`}
                onContextMenu={hasQuickMove ? (click) => { click.preventDefault(); preview.hide(); quickMove?.openMenu(event, click.currentTarget, onQuickMoveOpen); } : undefined}
                onClick={(clickEvent) => {
                    clickEvent.stopPropagation();
                    preview.hide(clickEvent.currentTarget);
                    onOpen(event);
                }}
                onMouseEnter={(mouseEvent) => preview.show(event, status, mouseEvent.currentTarget, 180, canDrag)}
                onMouseLeave={(mouseEvent) => preview.hide(mouseEvent.currentTarget)}
                onFocus={(focusEvent) => preview.show(event, status, focusEvent.currentTarget, 0, canDrag)}
                onBlur={(focusEvent) => preview.hide(focusEvent.currentTarget)}
            >
                {variant === 'month' ? (
                    <>
                        <time>{time}</time>
                        <span
                            className="smart-schedule__event-coach"
                            style={{ color: coachColor.foreground, background: coachColor.background }}
                        >
                            {event.coachName || '-'}
                        </span>
                        <span className="smart-schedule__event-title">
                            <span className="smart-schedule__event-name">{primaryText}</span>
                            {ptSessionText && <span className="smart-schedule__event-session">{ptSessionText}</span>}
                        </span>
                        {capacity && <span className="smart-schedule__event-capacity">{capacity}</span>}
                    </>
                ) : variant === 'agenda' ? (
                    <>
                        <span className="smart-schedule__event-type">{kindLabel}</span>
                        <span className="smart-schedule__event-title">
                            <span className="smart-schedule__event-name">{primaryText}</span>
                            {ptSessionText && <span className="smart-schedule__event-session">{ptSessionText}</span>}
                        </span>
                        <span
                            className="smart-schedule__event-coach-pill"
                            style={{ color: coachColor.foreground, background: coachColor.background, borderColor: coachColor.border }}
                        >
                            {event.coachName || '강사 미지정'}
                        </span>
                        <span className="smart-schedule__event-agenda-meta">
                            {statusHasIcon && <AlertCircle size={10} aria-hidden="true" />}
                            {[capacity ? `${capacity}명` : '', statusLabel].filter(Boolean).join(' · ')}
                        </span>
                    </>
                ) : variant === 'week-compact' ? (
                    <>
                        <span className={`smart-schedule__event-week-identity ${weekDetailText ? 'has-detail' : ''}`}>
                            <span
                                className="smart-schedule__event-coach-name"
                                style={{ color: coachColor.foreground }}
                            >
                                {event.coachName || '미지정'}
                            </span>
                            {weekDetailText && (
                                <>
                                    <span className="smart-schedule__event-week-separator" aria-hidden="true">·</span>
                                    <span className="smart-schedule__event-name">{weekDetailText}</span>
                                </>
                            )}
                        </span>
                        {weekSupplement && (
                            <span
                                className="smart-schedule__event-session smart-schedule__event-week-supplement"
                                title={weekSupplement}
                            >
                                {weekSupplement}
                            </span>
                        )}
                        {(continuesFromPreviousDay || statusHasIcon || isOpportunity) && (
                            <span className="smart-schedule__event-week-indicators">
                                {continuesFromPreviousDay && <span className="smart-schedule__event-continuation">이어짐</span>}
                                {status === 'pending' && (
                                    <span className="smart-schedule__event-week-status smart-schedule__event-week-status--pending">
                                        신청
                                    </span>
                                )}
                                {status === 'noshow' && (
                                    <span className="smart-schedule__event-week-status smart-schedule__event-week-status--noshow">
                                        노쇼
                                    </span>
                                )}
                                {isOpportunity && (
                                    <span className="smart-schedule__event-week-status smart-schedule__event-week-status--opportunity">
                                        신규
                                    </span>
                                )}
                            </span>
                        )}
                    </>
                ) : variant === 'day' ? (
                    <>
                        {event.kind === 'off' ? (
                            <span className="smart-schedule__event-day-off-line">
                                <span>{time}</span>
                                <span>{kindLabel}</span>
                            </span>
                        ) : isDayOverlapCompact ? (
                            <span className="smart-schedule__event-day-overlap-summary">
                                <strong>{firstMemberName || primaryText || '회원 미정'}</strong>
                                <small>{ptSessionText || time}</small>
                            </span>
                        ) : (
                            <span className="smart-schedule__event-day-content">
                                <span className="smart-schedule__event-day-primary-row">
                                    <strong className="smart-schedule__event-day-primary">{dayLines[0]}</strong>
                                    {continuesFromPreviousDay && <span className="smart-schedule__event-continuation">이어짐</span>}
                                    {dayBadgeLabel && (
                                        <span className="smart-schedule__event-day-badge">{dayBadgeLabel}</span>
                                    )}
                                </span>
                                {dayDetailLevel >= 2 && dayLines[1] && (
                                    <span className="smart-schedule__event-day-secondary">{dayLines[1]}</span>
                                )}
                                {dayDetailLevel >= 3 && dayLines[2] && (
                                    <span className="smart-schedule__event-day-tertiary">{dayLines[2]}</span>
                                )}
                            </span>
                        )}
                    </>
                ) : (
                    <>
                        <span className="smart-schedule__event-topline">
                            <span className="smart-schedule__event-time">{time}</span>
                            {continuesFromPreviousDay && <span className="smart-schedule__event-continuation">이어짐</span>}
                            <span className="smart-schedule__event-type">{kindLabel}</span>
                            {statusHasIcon && <AlertCircle size={12} aria-hidden="true" />}
                        </span>
                        <span className="smart-schedule__event-title">
                            <span className="smart-schedule__event-name">{primaryText}</span>
                            {ptSessionText && <span className="smart-schedule__event-session">{ptSessionText}</span>}
                        </span>
                        <span className="smart-schedule__event-meta">
                            <span
                                className="smart-schedule__event-coach-pill"
                                style={{ color: coachColor.foreground, background: coachColor.background, borderColor: coachColor.border }}
                            >
                                {event.coachName || '강사 미지정'}
                            </span>
                            {capacity ? <span>{capacity}명</span> : null}
                        </span>
                    </>
                )}
        </button>
    );
    return hasQuickMove ? <div className={`smart-schedule__event-shell smart-schedule__event-shell--${variant}`} style={style}>
        {card}<QuickMoveEntry event={event} compact onOpenSheet={onQuickMoveOpen} />
    </div> : card;
};

const areStylesEqual = (left?: CSSProperties, right?: CSSProperties) => {
    if (left === right) return true;
    if (!left || !right) return false;
    const leftKeys = Object.keys(left) as (keyof CSSProperties)[];
    const rightKeys = Object.keys(right) as (keyof CSSProperties)[];
    return leftKeys.length === rightKeys.length && leftKeys.every((key) => left[key] === right[key]);
};

const areEventCardPropsEqual = (left: EventCardProps, right: EventCardProps) =>
    left.event === right.event
    && left.onOpen === right.onOpen
    && left.variant === right.variant
    && left.className === right.className
    && left.displayTime === right.displayTime
    && left.dayDetailLevel === right.dayDetailLevel
    && left.continuesFromPreviousDay === right.continuesFromPreviousDay
    && left.status === right.status
    && left.onQuickMoveOpen === right.onQuickMoveOpen
    && areStylesEqual(left.style, right.style);

export default React.memo(EventCard, areEventCardPropsEqual);
